import { getSsrRequestLocals } from "@/config/ssr-request-locals";

declare global {
  interface Window {
    __PUBLIC_RUNTIME_CONFIG__?: PublicRuntimeConfigPayload;
  }
}

/** Populated server-side via layout.pug (see server/middlewares/public-runtime-config-locals.js). */
export interface PublicRuntimeConfigPayload {
  WEB_URL?: string;
  INTERCOM_APP_ID?: string;
  GTM_ID?: string;
  CAPTCHA_SITE_KEY?: string;
  FREE_TRIAL_DAYS?: string;
  FREE_TRIAL_UNTIL?: string;
  STRIPE_CURRENCY?: string;
  PLATFORM_SUBSCRIPTIONS_ENABLED?: boolean;
  /** When true, console shows Register AI Agent and mounts `/register-agent`. Off by default. */
  REGISTER_AGENT_ENABLED?: boolean;
  PAY_BY_INVOICE_ENABLED?: boolean;
  API_BASE_URL?: string;
  DISABLE_CHECKOUT?: boolean;
  MAINTENANCE_BANNER_ENABLED?: boolean;
  MAINTENANCE_BANNER_MESSAGE?: string;
  COOKIE_BANNER_ENABLED?: boolean;
  /** When false, apps hide the Instatus system-status indicator. On by default. */
  SYSTEM_STATUS_ENABLED?: boolean;
  /** When true, the admin console mounts the hidden `/sap-export` tool. Off by default. */
  SAP_EXPORT_TOOL_ENABLED?: boolean;
  /** True when a valid OneTrust domainScript is configured — the CMP owns consent. */
  ONETRUST_ENABLED?: boolean;
}

function rt(): PublicRuntimeConfigPayload | undefined {
  return typeof window !== "undefined" ? window.__PUBLIC_RUNTIME_CONFIG__ : getSsrRequestLocals()?.publicRuntimeConfig;
}

/** Prefer pod/runtime injection, then esbuild-defined `process.env` fallbacks (build artifact). */
export function getPublicWebUrl(): string {
  const fromRt = rt()?.WEB_URL;
  if (fromRt != null && fromRt !== "") return fromRt;
  try{
    return process.env.WEB_URL || "";
  } catch (error) {
    return "";
  }
}

export function getPublicApiBaseUrl(): string {
  const fromRt = rt()?.API_BASE_URL;
  if (fromRt != null && fromRt !== "") return fromRt;
  try{
    return process.env.API_BASE_URL || "";
  } catch (error) {
    return "";
  }
}

export function getPublicIntercomAppId(): string {
  const fromRt = rt()?.INTERCOM_APP_ID;
  if (fromRt != null && fromRt !== "") return fromRt;
  try{
    return process.env.INTERCOM_APP_ID || "";
  } catch (error) {
    return "";
  }
}

/** Google Tag Manager container id, used to bootstrap GTM after consent is granted. */
export function getPublicGtmId(): string {
  const fromRt = rt()?.GTM_ID;
  if (fromRt != null && fromRt !== "") return fromRt;
  try{
    return process.env.GTM_ID || "";
  } catch (error) {
    return "";
  }
}

export function getPublicCaptchaSiteKey(): string {
  const fromRt = rt()?.CAPTCHA_SITE_KEY;
  if (fromRt != null && fromRt !== "") return fromRt;
  try{
    return process.env.CAPTCHA_SITE_KEY || "";
  } catch (error) {
    return "";
  }
}

export function getPublicFreeTrialDays(): number {
  const fromRt = rt()?.FREE_TRIAL_DAYS;
  const raw = fromRt != null && String(fromRt).trim() !== "" ? String(fromRt) : (process.env.FREE_TRIAL_DAYS ?? "");
  if (raw === "") return 14;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 14;
}

export function getPublicFreeTrialUntil(): string {
  const fromRt = rt()?.FREE_TRIAL_UNTIL;
  if (fromRt != null && fromRt !== "") return fromRt;
  try{
    return process.env.FREE_TRIAL_UNTIL || "";
  } catch (error) {
    return "";
  }
}

/** ISO 4217 for Intl (uppercase). */
export function getPublicStripeCurrencyCode(): string {
  const fromRt = rt()?.STRIPE_CURRENCY;
  const raw =
    fromRt != null && String(fromRt).trim() !== "" ? String(fromRt).trim() : (process.env.STRIPE_CURRENCY ?? "");
  const code = raw !== "" ? raw : "usd";
  return code.toUpperCase();
}

/** Lowercase slug (Stripe-style). */
export function getPublicStripeCurrencyLower(): string {
  return getPublicStripeCurrencyCode().toLowerCase();
}

/** Same semantics as legacy feature flag parsing. */
export function getPublicPlatformSubscriptionsEnabled(): boolean {
  const rtVal = rt()?.PLATFORM_SUBSCRIPTIONS_ENABLED;
  if (typeof rtVal === "boolean") {
    return rtVal;
  }
  try{
    const e = process.env.PLATFORM_SUBSCRIPTIONS_ENABLED;
    return e === "true";
  } catch (error) {
    return false;
  }
  return e === "true";
}

/** Off by default — enable via `features.registerAgentEnabled` or `REGISTER_AGENT_ENABLED`. */
export function getPublicRegisterAgentEnabled(): boolean {
  const rtVal = rt()?.REGISTER_AGENT_ENABLED;
  if (typeof rtVal === "boolean") {
    return rtVal;
  }
  try{
  const e = process.env.REGISTER_AGENT_ENABLED;
    return e === "true";
  } catch (error) {
    return false;
  }
}

export function getPublicPayByInvoiceEnabled(): boolean {
  const rtVal = rt()?.PAY_BY_INVOICE_ENABLED;
  if (typeof rtVal === "boolean") {
    return rtVal;
  }
  try{
    const e = process.env.PAY_BY_INVOICE_ENABLED;
    return e === "true";
  } catch (error) {
    return false;
  }
  return e === "true";
}

export function getPublicDisableCheckout(): boolean {
  const rtVal = rt()?.DISABLE_CHECKOUT;
  if (typeof rtVal === "boolean") {
    return rtVal;
  }
  try{
  const e = process.env.DISABLE_CHECKOUT;
    return e === "true";
  } catch (error) {
    return false;
  }
}

export function getPublicMaintenanceBannerEnabled(): boolean {
  const rtVal = rt()?.MAINTENANCE_BANNER_ENABLED;
  if (typeof rtVal === "boolean") {
    return rtVal;
  }
  try{
  const e = process.env.MAINTENANCE_BANNER_ENABLED;
    return e === "true";
  } catch (error) {
    return false;
  }
}

export function getPublicMaintenanceBannerMessage(): string {
  const fromRt = rt()?.MAINTENANCE_BANNER_MESSAGE;
  if (fromRt != null && String(fromRt).trim() !== "") {
    return String(fromRt).trim();
  }
  try{
    return process.env.MAINTENANCE_BANNER_MESSAGE?.trim() || "";
  } catch (error) {
    return "";
  }
}

/** On by default — set `features.systemStatusEnabled: false` or `SYSTEM_STATUS_ENABLED=false` to hide. */
export function getPublicSystemStatusEnabled(): boolean {
  const rtVal = rt()?.SYSTEM_STATUS_ENABLED;
  if (typeof rtVal === "boolean") {
    return rtVal;
  }
  try {
    // esbuild `define` injects a boolean (`true`/`false`), not the string `"false"`.
    const e = process.env.SYSTEM_STATUS_ENABLED as unknown;
    return e !== false && e !== "false";
  } catch (error) {
    return true;
  }
}

/** Same semantics as legacy feature flag parsing. */
export function getPublicSapExportToolEnabled(): boolean {
  const rtVal = rt()?.SAP_EXPORT_TOOL_ENABLED;
  if (typeof rtVal === "boolean") {
    return rtVal;
  }
  try {
    const e = process.env.SAP_EXPORT_TOOL_ENABLED;
    return e === "true";
  } catch (error) {
    return false;
  }
}

export function getPublicCookieBannerEnabled(): boolean {
  const rtVal = rt()?.COOKIE_BANNER_ENABLED;
  if (typeof rtVal === "boolean") {
    return rtVal;
  }
  try{
    const e = process.env.COOKIE_BANNER_ENABLED;
    return e === "true";
  } catch (error) {
    return false;
  }
}

/**
 * True when OneTrust is deployed, in which case it owns consent and our own
 * banner stands down. Server-injected only — there's no build-time fallback,
 * because it must track the same validated domainScript that decides whether
 * the OneTrust script tag is rendered at all.
 */
export function getPublicOneTrustEnabled(): boolean {
  return rt()?.ONETRUST_ENABLED === true;
}
